import { useEffect, useRef } from 'react';
import { useSelector } from '@legendapp/state/react';
import { SEARCH_MODES } from '../../constants/searchModes';
import {
  panelSearch$,
  setPanelSearchDraft,
  setPanelSearchKeywordInput,
  setPanelSearchMode,
} from '../../store/panelSearch$';

const KEYWORD_DEBOUNCE_MS = 300;

/**
 * The two-mode search surface behind one input: keyword search and RAG chat.
 *
 * Both the library panel and the deck panel offer this, so the switching rules
 * live here rather than being written twice. Each host supplies a stable
 * `scopeKey` (see `store/panelSearch$`) plus the keyword destination and
 * debounce cadence: the library owns a global result list, while the deck owns
 * an in-place card filter.
 *
 * Mode and composer text are session-scoped per scope instead of component
 * state, so they survive panel close/reopen and tab switches, stay isolated
 * between scopes, and never reach storage. The keyword field (what keyword
 * mode is committing) and the AI draft are kept separately: switching modes
 * cannot eat a half-written question, and coming back restores each side's
 * own text.
 *
 * `keyword` / `setKeyword` name whichever query destination the surface owns;
 * the hook does not know or care whether that is a result list or an in-place
 * filter.
 */
export const useSearchModes = ({ scopeKey, keyword, setKeyword, inputRef, debounceMs = KEYWORD_DEBOUNCE_MS }) => {
  const mode = useSelector(() => panelSearch$[scopeKey]?.mode.get() ?? SEARCH_MODES.AI);
  const draft = useSelector(() => panelSearch$[scopeKey]?.draft.get() ?? '');
  const keywordInput = useSelector(() => panelSearch$[scopeKey]?.keywordInput.get() ?? '');
  const debounceRef = useRef(null);

  const isKeyword = mode === SEARCH_MODES.KEYWORD;

  // A pending keyword commit belongs to the scope it was typed in; dropping the
  // scope (or unmounting) must not fire it into a destination that changed.
  // Reads the ref at cleanup time, so the newest pending timer is the one killed.
  useEffect(() => () => clearTimeout(debounceRef.current), [scopeKey]);

  const changeMode = (nextMode) => {
    clearTimeout(debounceRef.current);
    // Entering keyword mode takes over the list's already-active filter word
    // (the card list's own search bar writes it too), as long as this scope's
    // keyword input doesn't have text of its own.
    if (nextMode === SEARCH_MODES.KEYWORD && keyword && !panelSearch$[scopeKey]?.keywordInput.peek()) {
      setPanelSearchKeywordInput(scopeKey, keyword);
    }
    setPanelSearchMode(scopeKey, nextMode);
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const setInput = (value) => {
    if (isKeyword) setPanelSearchKeywordInput(scopeKey, value);
    else setPanelSearchDraft(scopeKey, value);
  };

  const changeInput = (value) => {
    setInput(value);
    if (!isKeyword) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setKeyword(value), debounceMs);
  };

  const commitKeyword = () => {
    clearTimeout(debounceRef.current);
    setKeyword(keywordInput);
  };

  const clearKeyword = () => {
    clearTimeout(debounceRef.current);
    setPanelSearchKeywordInput(scopeKey, '');
    setKeyword('');
    inputRef.current?.focus();
  };

  return {
    mode,
    isKeyword,
    input: isKeyword ? keywordInput : draft,
    setInput,
    changeMode,
    changeInput,
    commitKeyword,
    clearKeyword,
  };
};

export default useSearchModes;
